import { Button, Textarea, toast } from "@medusajs/ui"
import { useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { EmptyState, ErrorState, PageHeader, SearchInput } from "@/components/ui"
import { errorMessage } from "@/lib/sdk"
import { AREA, useIssueActions, useIssues, type Issue, type IssueSeverity } from "@/lib/issues"
import { Badge, FilterBar, FilterSelect, LoadingCard } from "@/terabizz"

/**
 * Admin → Issues (backend lib/issues.ts): everything that went wrong and needs a person, most
 * serious first. A failed payment that may have taken money, an order ShipStation never got, a
 * renewal that charged with no order, an order email that didn't go out. Each row says who it
 * is about and links to the order or customer; "Retry" re-runs the failed step where that is
 * safe, otherwise fix it by hand and mark it resolved. Rows close themselves when a later
 * attempt works. New money and stuck-order problems are also emailed to the staff recipients.
 */

const PAGE = 50
const SEV: Record<IssueSeverity, { label: string; tone: "critical" | "attention" | "neutral"; hint: string }> = {
  critical: { label: "Money", tone: "critical", hint: "Money may have been taken without an order, or a refund may not have gone through." },
  high: { label: "Stuck", tone: "attention", hint: "An order, shipment, subscription or customer is stuck until someone acts." },
  normal: { label: "Minor", tone: "neutral", hint: "Worth a look when convenient." },
}
const when = (s: string) => new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
const money = (n: number) => `$${n.toFixed(2)}`

export function IssuesPage() {
  const [params, setParams] = useSearchParams()
  const get = (k: string) => params.get(k) ?? ""
  const page = Math.max(0, Number(params.get("page") ?? 1) - 1)
  const status = get("status") || "open"
  const query = {
    limit: String(PAGE),
    offset: String(page * PAGE),
    status,
    ...(get("q") ? { q: get("q") } : {}),
    ...(get("area") ? { area: get("area") } : {}),
    ...(get("severity") ? { severity: get("severity") } : {}),
  }
  const qs = new URLSearchParams(query).toString()
  const { data, isLoading, isFetching, error } = useIssues(qs)
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k)
    if (!("page" in patch)) next.delete("page")
    setParams(next, { replace: true })
  }
  const c = data?.counts
  const count = data?.count ?? 0
  const filtered = !!(get("q") || get("area") || get("severity") || status !== "open")

  return (
    <>
      <PageHeader title="Issues" subtitle="Everything that failed and needs someone: payments, orders, shipments, subscriptions, emails. Most serious first." />
      {c ? (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(
            [
              ["Open", c.open, { severity: null, status: null }],
              ["Money", c.critical, { severity: "critical", status: null }],
              ["Stuck", c.high, { severity: "high", status: null }],
              ["Minor", c.normal, { severity: "normal", status: null }],
            ] as [string, number, Record<string, string | null>][]
          ).map(([label, n, f]) => (
            <button
              key={label}
              type="button"
              onClick={() => set({ q: null, area: null, ...f })}
              className={`tf-card rounded-[10px] border bg-[var(--tb-surface,#fff)] px-3 py-2.5 text-left hover:bg-[var(--tb-surface-hover,#f7f7f7)] ${label === "Money" && n ? "border-[color:var(--k-crit-line)]" : "border-[var(--tb-line)]"}`}
            >
              <span className="block text-[12px] text-[var(--tb-ink-3)]">{label}</span>
              <span className={`block text-[20px] font-semibold tabular-nums ${label === "Money" && n ? "text-[var(--tb-critical-fg,#8e1f0b)]" : "text-[var(--tb-ink)]"}`}>{n.toLocaleString()}</span>
            </button>
          ))}
        </div>
      ) : null}

      {data?.payments_being_checked?.length ? <PaymentsBeingChecked rows={data.payments_being_checked} /> : null}

      <div className="tf-card overflow-hidden rounded-[12px] border border-[var(--tb-line)] bg-[var(--tb-surface,#fff)]">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 [&>div]:max-w-none">
          <SearchInput value={get("q")} onChange={(v) => set({ q: v || null })} placeholder="Search by email, order, cart, transaction id or error" />
        </div>
        <FilterBar>
          <FilterSelect label="Status" value={status} onChange={(v) => set({ status: v && v !== "open" ? v : null })} options={[["open", "Open"], ["resolved", "Resolved"], ["ignored", "Ignored"], ["all", "All"]]} />
          <FilterSelect label="Area" value={get("area")} onChange={(v) => set({ area: v || null })} options={Object.entries(AREA).map(([k, v]) => [k, `${v}${c?.by_area[k] ? ` (${c.by_area[k]})` : ""}`])} />
          <FilterSelect label="Type" value={get("severity")} onChange={(v) => set({ severity: v || null })} options={(Object.keys(SEV) as IssueSeverity[]).map((k) => [k, SEV[k].label])} />
          {filtered && (
            <button type="button" className="txt-compact-small ml-auto text-ui-fg-interactive hover:underline" onClick={() => set({ q: null, area: null, severity: null, status: null })}>
              Clear filters
            </button>
          )}
        </FilterBar>
        {error ? (
          <ErrorState error={error} />
        ) : isLoading ? (
          <div className="p-4"><LoadingCard lines={6} /></div>
        ) : !data?.issues.length ? (
          <EmptyState
            title={status === "open" && !filtered ? "Nothing needs attention" : "No issues found"}
            body={status === "open" && !filtered ? "When a payment, order, shipment, subscription or email fails, it shows up here with what's needed to fix it." : "Try clearing the filters."}
          />
        ) : (
          <ul className={isFetching ? "opacity-60 transition-opacity" : "transition-opacity"} aria-busy={isFetching}>
            {data.issues.map((i) => <Row key={i.id} i={i} onFilter={set} />)}
          </ul>
        )}
        {count > 0 && (
          <div className="flex items-center justify-between border-t border-[var(--tb-line)] px-4 py-2.5">
            <span className="txt-compact-small tabular-nums text-[var(--tb-ink-3)]">{page * PAGE + 1}–{Math.min(count, (page + 1) * PAGE)} of {count.toLocaleString()}</span>
            <div className="flex gap-x-2">
              <Button size="small" variant="secondary" disabled={page === 0} onClick={() => set({ page: page > 1 ? String(page) : null })}>Previous</Button>
              <Button size="small" variant="secondary" disabled={(page + 1) * PAGE >= count} onClick={() => set({ page: String(page + 2) })}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/** Checkout charges whose outcome the store is still confirming with Authorize.net (lib/payment-recovery.ts). */
function PaymentsBeingChecked({ rows }: { rows: { id: string; cart_id: string; amount: number; email: string | null; transaction_id: string | null; result: string | null; last_error: string | null; created_at: string }[] }) {
  const RESULT: Record<string, string> = {
    in_flight: "Checkout in progress or interrupted",
    no_answer: "Authorize.net didn't answer",
    approved_then_failed: "Approved, then the order failed",
    too_early: "Waiting for Authorize.net's records",
    release_failed: "Couldn't give the money back: refund in Authorize.net",
  }
  return (
    <div className="mb-4 rounded-[12px] border border-[color:var(--k-warn-line)] bg-[color:var(--k-warn-bg-soft)] px-4 py-3">
      <p className="text-[13px] font-semibold text-[var(--tb-ink)]">Checkout payments being confirmed ({rows.length})</p>
      <p className="mb-2 text-[12px] text-[var(--tb-ink-3)]">The store checks these with Authorize.net every 15 minutes. Money taken without an order is given back automatically. Anything it can't fix shows up below as an issue.</p>
      <ul className="text-[12px]">
        {rows.slice(0, 10).map((r) => (
          <li key={r.id} className="flex flex-wrap gap-x-3 border-t border-[color:var(--k-warn-line-faint)] py-1 first:border-t-0">
            <span className="tabular-nums text-[var(--tb-ink-3)]">{when(r.created_at)}</span>
            <span>{r.email ?? "No email yet"}</span>
            <span className="tabular-nums">{money(r.amount)}</span>
            <span className="text-[var(--tb-ink-3)]">{RESULT[(r.result ?? "").replace(/:.*/, "")] ?? r.result ?? "Checking"}</span>
            {r.transaction_id ? <span className="font-mono text-[var(--tb-ink-3)]">txn {r.transaction_id}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

function Row({ i, onFilter }: { i: Issue; onFilter: (p: Record<string, string | null>) => void }) {
  const [open, setOpen] = useState(i.severity === "critical" && i.status === "open")
  const [note, setNote] = useState("")
  const { retry, setStatus } = useIssueActions()
  const sev = SEV[i.severity] ?? SEV.normal
  const doRetry = () =>
    retry.mutate(i.id, {
      onSuccess: (r) => (r.ok ? toast.success(r.message) : toast.error(r.message)),
      onError: (e) => toast.error(errorMessage(e)),
    })
  const mark = (status: "resolved" | "ignored" | "open") =>
    setStatus.mutate(
      { id: i.id, status, note: note.trim() || undefined },
      { onSuccess: () => (toast.success(status === "open" ? "Reopened" : status === "resolved" ? "Marked resolved" : "Ignored"), setNote("")), onError: (e) => toast.error(errorMessage(e)) },
    )
  const ctx = Object.entries(i.context ?? {}).filter(([, v]) => v !== null && v !== undefined && v !== "")

  return (
    <li className="border-t border-[var(--tb-line)] first:border-t-0">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="grid w-full grid-cols-[1fr_auto] items-start gap-x-3 px-4 py-2.5 text-left hover:bg-[var(--tb-surface-hover,#f7f7f7)] sm:grid-cols-[130px_1fr_auto]">
        <span className="hidden text-[12px] tabular-nums text-[var(--tb-ink-3)] sm:block">{when(i.last_seen_at)}</span>
        <span className="min-w-0">
          <span className="block text-[13px] text-[var(--tb-ink)]">{i.title}</span>
          <span className="block truncate text-[12px] text-[var(--tb-ink-3)]">
            {AREA[i.area] ?? i.area}
            {i.email ? ` · ${i.email}` : ""}
            {i.amount != null ? ` · ${money(i.amount)}` : ""}
            {i.occurrences > 1 ? ` · happened ${i.occurrences} times` : ""}
          </span>
          <span className="block text-[12px] tabular-nums text-[var(--tb-ink-3)] sm:hidden">{when(i.last_seen_at)}</span>
        </span>
        <span className="flex flex-col items-end gap-1 pt-0.5">
          <Badge tone={sev.tone}>{sev.label}</Badge>
          {i.status !== "open" ? <span className="text-[11px] text-[var(--tb-ink-3)]">{i.status === "resolved" ? "Resolved" : "Ignored"}</span> : null}
        </span>
      </button>
      {open && (
        <div className="mx-4 mb-3 rounded-[8px] bg-[var(--tb-canvas)] px-3 py-2.5 text-[12px] sm:ml-[146px]">
          <p className="mb-2 text-[var(--tb-ink-3)]">{sev.hint}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            {i.order_id ? (<><dt className="text-[var(--tb-ink-3)]">Order</dt><dd><Link className="text-ui-fg-interactive hover:underline" to={`/orders/${i.order_id}`}>Open the order</Link> <span className="font-mono text-[var(--tb-ink-3)]">{i.order_id}</span></dd></>) : null}
            {i.customer_id ? (<><dt className="text-[var(--tb-ink-3)]">Customer</dt><dd><Link className="text-ui-fg-interactive hover:underline" to={`/customers/${i.customer_id}`}>Open the customer</Link></dd></>) : null}
            {i.email ? (<><dt className="text-[var(--tb-ink-3)]">Email</dt><dd className="break-all"><button type="button" className="text-ui-fg-interactive hover:underline" onClick={() => onFilter({ q: i.email, status: "all" })}>{i.email}</button> <span className="text-[var(--tb-ink-3)]">(every issue for this address)</span></dd></>) : null}
            {i.subscription_id ? (<><dt className="text-[var(--tb-ink-3)]">Subscription</dt><dd><Link className="text-ui-fg-interactive hover:underline" to={`/apps/subscriptions/subscriptions/${i.subscription_id}`}>Open the subscription</Link></dd></>) : null}
            {i.amount != null ? (<><dt className="text-[var(--tb-ink-3)]">Amount</dt><dd className="tabular-nums">{money(i.amount)}</dd></>) : null}
            {i.transaction_id ? (<><dt className="text-[var(--tb-ink-3)]">Authorize.net</dt><dd className="font-mono">transaction {i.transaction_id}</dd></>) : null}
            {i.cart_id ? (<><dt className="text-[var(--tb-ink-3)]">Cart</dt><dd className="font-mono">{i.cart_id}</dd></>) : null}
            {i.detail ? (<><dt className="text-[var(--tb-ink-3)]">Error</dt><dd className="whitespace-pre-wrap break-words text-[var(--tb-critical-fg,#8e1f0b)]">{i.detail}</dd></>) : null}
            {ctx.length ? (<><dt className="text-[var(--tb-ink-3)]">Details</dt><dd className="break-all font-mono text-[11px]">{ctx.map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ")}</dd></>) : null}
            <dt className="text-[var(--tb-ink-3)]">First seen</dt>
            <dd className="tabular-nums">{new Date(i.first_seen_at).toLocaleString()}{i.occurrences > 1 ? ` · last ${new Date(i.last_seen_at).toLocaleString()} (${i.occurrences}×)` : ""}</dd>
            <dt className="text-[var(--tb-ink-3)]">Type</dt>
            <dd className="font-mono text-[var(--tb-ink-3)]">{i.kind}</dd>
            {i.last_retry_result ? (<><dt className="text-[var(--tb-ink-3)]">Last retry</dt><dd>{i.last_retry_at ? `${when(i.last_retry_at)}: ` : ""}{i.last_retry_result}</dd></>) : null}
            {i.resolution ? (<><dt className="text-[var(--tb-ink-3)]">{i.status === "open" ? "Note" : "Resolved"}</dt><dd>{i.resolution}{i.resolved_at ? ` · ${when(i.resolved_at)}` : ""}</dd></>) : null}
          </dl>
          {i.status === "open" ? (
            <div className="mt-3 flex flex-col gap-2 border-t border-[var(--tb-line)] pt-3">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What did you do? (optional, kept with the issue)" />
              <div className="flex flex-wrap gap-2">
                {i.retry_label ? <Button size="small" onClick={doRetry} isLoading={retry.isPending}>{i.retry_label}</Button> : null}
                <Button size="small" variant="secondary" onClick={() => mark("resolved")} isLoading={setStatus.isPending}>Mark resolved</Button>
                <Button size="small" variant="transparent" onClick={() => mark("ignored")} disabled={setStatus.isPending}>Ignore</Button>
              </div>
            </div>
          ) : (
            <div className="mt-3 border-t border-[var(--tb-line)] pt-3">
              <Button size="small" variant="secondary" onClick={() => mark("open")} isLoading={setStatus.isPending}>Reopen</Button>
            </div>
          )}
        </div>
      )}
    </li>
  )
}
